/**
 * UnitCombobox — Combobox unité pour les lignes de devis (Bug 4 récurrent).
 *
 * Comportement exigé :
 * - CLIC sur le champ → liste complète s'ouvre IMMÉDIATEMENT (que la valeur soit vide ou pré-remplie).
 * - Filtrage à la saisie mais rien à effacer manuellement pour voir les options.
 * - Saisie libre autorisée (unité custom métier absente de la liste).
 * - Sélection d'une option → close + commit + focus perdu.
 *
 * Implémentation : Popover controlled + shadcn Command list.
 * Le trigger porte un vrai <input> pour permettre saisie/filtrage, mais onClick force open=true systématiquement.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

const UNITS = ["m²", "mc", "ml", "pièce", "pièces", "forfait", "h", "kWc", "kW", "l"];

export default function UnitCombobox({ value, onChange, disabled, className, "data-testid": dtid }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [hasTyped, setHasTyped] = useState(false);
  const inputRef = useRef(null);

  // Sync externe uniquement quand le popover est fermé (évite d'écraser la frappe)
  useEffect(() => {
    if (!open) {
      setDraft(value ?? "");
      setHasTyped(false);
    }
  }, [value, open]);

  const filtered = useMemo(() => {
    // Exigence Bug 4 : au 1er clic, liste COMPLÈTE (peu importe le contenu du champ).
    // On ne filtre qu'après une vraie frappe utilisateur (hasTyped=true).
    if (!hasTyped) return UNITS;
    const q = draft.trim().toLowerCase();
    if (!q) return UNITS;
    const exact = UNITS.filter((u) => u.toLowerCase() === q);
    const starts = UNITS.filter((u) => u.toLowerCase() !== q && u.toLowerCase().startsWith(q));
    const contains = UNITS.filter(
      (u) => u.toLowerCase() !== q && !u.toLowerCase().startsWith(q) && u.toLowerCase().includes(q),
    );
    return [...exact, ...starts, ...contains];
  }, [draft, hasTyped]);

  const commit = (v) => {
    const final = (v ?? "").trim() || "unité";
    setDraft(final);
    onChange(final);
    setOpen(false);
    inputRef.current?.blur();
  };

  return (
    <Popover open={open} onOpenChange={(o) => setOpen(o && !disabled)}>
      <PopoverAnchor asChild>
        <div className={cn("relative", className)}>
          <input
            ref={inputRef}
            type="text"
            value={draft}
            disabled={disabled}
            onClick={() => setOpen(true)}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setDraft(e.target.value);
              setHasTyped(true);
              if (!open) setOpen(true);
            }}
            onBlur={() => {
              // délai pour laisser cliquer sur une option avant fermeture
              setTimeout(() => {
                if (draft !== value) onChange(draft.trim() || "unité");
              }, 120);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (filtered.length > 0) commit(filtered[0]);
                else commit(draft);
              } else if (e.key === "Escape") {
                setOpen(false);
              }
            }}
            className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 pr-8 py-2 text-sm text-slate-100 outline-none focus:border-slate-500 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid={dtid}
            autoComplete="off"
            inputMode="text"
          />
          <ChevronDown
            className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
            aria-hidden="true"
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-[--radix-popover-anchor-width] min-w-[180px] border border-slate-700 bg-slate-900 p-1 text-slate-100"
        align="start"
        sideOffset={2}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <ul className="max-h-56 overflow-y-auto" data-testid={`${dtid}-list`}>
          {filtered.length === 0 ? (
            <li className="px-2 py-1.5 text-xs text-slate-500">
              Aucune unité prédéfinie — « {draft.trim()} » sera utilisée (custom).
            </li>
          ) : (
            filtered.map((u) => (
              <li
                key={u}
                onMouseDown={(e) => {
                  // mouseDown évite le blur avant sélection
                  e.preventDefault();
                  commit(u);
                }}
                className={cn(
                  "cursor-pointer rounded px-2 py-1.5 text-sm hover:bg-slate-800",
                  u === draft ? "bg-slate-800/60 font-semibold text-white" : "",
                )}
                data-testid={`${dtid}-option-${u}`}
              >
                <span className="mr-2 inline-flex items-center">
                  {u === draft ? <Check className="h-3.5 w-3.5 text-[#C4553B]" /> : null}
                </span>
                {u}
              </li>
            ))
          )}
        </ul>
        <div className="mt-1 border-t border-slate-800 px-2 py-1.5 text-[10px] text-slate-500">
          Vous pouvez aussi saisir une unité personnalisée.
        </div>
      </PopoverContent>
    </Popover>
  );
}
